import { filterBy } from '../../helpers/js-utils';
import { sendEmptyEventDisableSlots } from './ad-services.utils';
import DebugUANC from '../../core/version/debug';
import { IServices } from '../../uanc.types';

/**
 * Przekazuje sloty bez włączonego LL.
 *
 * @param {T - ISlot[] | IDFPServiceSlot[] | IAOServiceSlot} slots - sloty
 * @returns {ISlot[]} przefiltorwane sloty - bez LL
 */
const getNotLazyLoadingSlots = <T extends { isLazyLoading: boolean }>(slots: T[]): T[] => {
    return filterBy(slots, 'isLazyLoading', false);
};

/**
 * Weryfikacja atrybutów dla sitebrandingu. W zależności od przypadku ewentualne wyłączenie slotu.
 *
 * @param {T - IDFPServiceSlot | IAOServiceSlot} slot - slot do zdefiniowania
 * @returns {boolean} zwraca informację czy slot sitebranding może być osadzony
 */
const isSitebrandingSlotToDefined = <
    T extends {
        name: string;
        disable: boolean;
        isTest: boolean;
        placementIds: string[];
    },
>(
    slot: T
): boolean => {
    const { detection, UANC } = window.Inpl.Ad;
    const services = window.Inpl.Ad.services as IServices;
    try {
        if (!services.TesterService?._testId) {
            if (document.getElementById('__next') && !document.getElementById(slot.placementIds[0])) {
                throw new Error(`placement "${slot.placementIds[0]}" does not exist.`);
            }
            if (
                document.getElementById(slot.placementIds[0])?.dataset?.adSitebrandingPh &&
                !location.search.includes('testsite') &&
                !location.search.includes('google_preview')
            ) {
                const sponsCont: Element | null = document.querySelector('#sponsCont');
                if (!sponsCont) {
                    throw new Error('container "#sponsCont" does not exist.');
                }

                const isMobileAttribute: boolean = sponsCont.getAttribute('data-ad-mobile') === '1';
                const isDesktopAttribute: boolean = sponsCont.getAttribute('data-ad-desktop') === '1';
                if ((detection!.isMobile && !isMobileAttribute) || (!detection!.isMobile && !isDesktopAttribute)) {
                    throw new Error(
                        `problem with attributes ${detection!.isMobile && !isMobileAttribute ? 'data-ad-mobile' : 'data-ad-desktop'}.`
                    );
                }
                return true;
            }
        }
        return true;
    } catch (e) {
        slot.disable = true;
        UANC instanceof DebugUANC && UANC.warn(`Sitebranding cannot be defined - ${e}`);
        return false;
    }
};

/**
 * Zwraca sloty, które mogą zostać przekazane do osadzenia.
 *
 * @param {T - IDFPServiceSlot[] | IAOServiceSlot[]} slotsToVerify - konfiguracje slotów
 * @returns {T[]} przefiltrowane sloty, które mogą zostać zdefiniowane i wyświetlone
 */
export const verifySlotsToDefined = <
    T extends {
        name: string;
        disable: boolean;
        isTest: boolean;
        placementIds: string[];
    },
>(
    slotsToVerify: T[]
): T[] => {
    return slotsToVerify.filter((slot: T) => isSlotCanBeEmbed(slot));
};

/**
 * Weryfikuje czy dany slot może zostać zdefiniowany. W przypadku gdy slot jest wyłączony inicjuje wysyłkę eventu 'Inpl.empty'.
 *
 * @param {T - IDFPServiceSlot | IAOServiceSlot} slotToVerify - konfiguracje slotów
 * @returns {boolean} informacja czy slot może zostać zdefiniowany i wyświetlony
 */
export const isSlotCanBeEmbed = <
    T extends {
        name: string;
        disable: boolean;
        isTest: boolean;
        placementIds: string[];
    },
>(
    slotToVerify: T
): boolean => {
    if (slotToVerify.name === 'sitebranding' && !isSitebrandingSlotToDefined(slotToVerify)) return false;
    if (slotToVerify.disable) sendEmptyEventDisableSlots(slotToVerify);

    return !slotToVerify.disable && !slotToVerify.isTest && !!slotToVerify.placementIds.length;
};

/**
 * Przekazuje sloty z włączonym LL.
 *
 * @param {T - ISlot[] | IDFPServiceSlot[]} slots - sloty
 * @returns {ISlot[]} przefiltorwane sloty
 */
const getLazyLoadingSlots = <T extends { isLazyLoading: boolean }>(slots: T[]): T[] => {
    return filterBy(slots, 'isLazyLoading', true);
};

export { getNotLazyLoadingSlots, getLazyLoadingSlots };
